import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { ArrowLeft, TrendingUp, DollarSign, Calendar, Tag } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { expensesAPI } from '../../services/api';
import { format } from 'date-fns';
import { KpiChip } from '../../components/ui';

const COLORS = ['#0d9488', '#059669', '#10b981', '#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5'];

export default function ExpenseAnalytics() {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [summary, setSummary] = useState({ total: 0 });
  const [byCategory, setByCategory] = useState([]);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      expensesAPI.getSummary(month, year),
      expensesAPI.getByCategory(month, year),
      expensesAPI.getMonthlyTrend(12),
    ]).then(([sumRes, catRes, trendRes]) => {
      setSummary(sumRes.data || { total: 0 });
      setByCategory((catRes.data || []).map((r) => ({
        name: r.category_name,
        value: parseFloat(r.total) || 0,
      })));
      setMonthlyTrend((trendRes.data || []).map((r) => ({
        month: format(new Date(r.month), 'MMM yy'),
        total: parseFloat(r.total) || 0,
      })));
    }).catch(console.error).finally(() => setLoading(false));
  }, [month, year]);

  const avgExpense = monthlyTrend.length > 0
    ? monthlyTrend.reduce((s, r) => s + r.total, 0) / monthlyTrend.length
    : 0;
  const highestCategory = byCategory.length > 0
    ? byCategory.reduce((a, b) => (a.value > b.value ? a : b), byCategory[0])
    : null;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Link
              to="/dashboard/expenses"
              aria-label="Back to expenses"
              className="shrink-0 p-2 rounded-lg text-ga-text-primary hover:bg-ga-bg-surface-subtle"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
                <TrendingUp className="h-7 w-7 text-ga-primary" />
                Expense Analytics
              </h1>
              <p className="text-ga-text-muted mt-1">Charts and KPIs</p>
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value, 10))}
              className="ga-input w-32 py-2 rounded-lg"
              aria-label="Month"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                <option key={m} value={m}>{format(new Date(2000, m - 1, 1), 'MMMM')}</option>
              ))}
            </select>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="ga-input w-24 py-2 rounded-lg"
              aria-label="Year"
            >
              {[year, year - 1, year - 2].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <KpiChip
            label="Monthly Expense"
            value={`₹${Number(summary.total || 0).toLocaleString()}`}
            icon={DollarSign}
            accent="primary"
          />
          <KpiChip
            label="Average (12 months)"
            value={`₹${avgExpense.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
            icon={Calendar}
            accent="info"
          />
          <KpiChip
            label="Highest Category"
            value={highestCategory ? highestCategory.name : '—'}
            icon={Tag}
            accent="warning"
          />
        </div>

        {loading ? (
          <div className="ga-card rounded-xl p-12 text-center text-ga-text-muted">Loading...</div>
        ) : (
          <>
            <div className="ga-card rounded-xl p-4 min-w-0">
              <h2 className="text-lg font-semibold mb-4 text-ga-text-primary">Monthly Expense Trend</h2>
              {/* min-w-0 + explicit size fixes Recharts ResponsiveContainer getting 0 width in flex layouts */}
              <div className="h-64 w-full min-w-0">
                {monthlyTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} debounce={50}>
                    <BarChart data={monthlyTrend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--ga-border-subtle)" />
                      <XAxis dataKey="month" stroke="var(--ga-text-muted)" tick={{ fontSize: 12 }} />
                      <YAxis
                        stroke="var(--ga-text-muted)"
                        tick={{ fontSize: 12 }}
                        width={56}
                        tickFormatter={(v) => `₹${v}`}
                      />
                      <Tooltip
                        contentStyle={{
                          background: 'var(--ga-bg-surface)',
                          border: '1px solid var(--ga-border-subtle)',
                          borderRadius: 8,
                          color: 'var(--ga-text-primary)',
                        }}
                        formatter={(v) => [`₹${Number(v).toLocaleString()}`, 'Total']}
                      />
                      <Bar dataKey="total" fill="#0d9488" radius={[4, 4, 0, 0]} maxBarSize={48} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-full items-center justify-center text-ga-text-muted">No trend data yet</div>
                )}
              </div>
            </div>

            <div className="ga-card rounded-xl p-4 min-w-0">
              <h2 className="text-lg font-semibold mb-4 text-ga-text-primary">Expense by Category</h2>
              <div className="h-72 w-full min-w-0 sm:h-80">
                {byCategory.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} debounce={50}>
                    <PieChart>
                      <Pie
                        data={byCategory}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="45%"
                        outerRadius="55%"
                        labelLine={false}
                        label={({ percent }) => (percent >= 0.08 ? `${Math.round(percent * 100)}%` : '')}
                      >
                        {byCategory.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: 'var(--ga-bg-surface)',
                          border: '1px solid var(--ga-border-subtle)',
                          borderRadius: 8,
                          color: 'var(--ga-text-primary)',
                        }}
                        formatter={(v, name) => [`₹${Number(v).toLocaleString()}`, name]}
                      />
                      <Legend
                        verticalAlign="bottom"
                        formatter={(value) => {
                          const row = byCategory.find((c) => c.name === value);
                          return row ? `${value} (₹${row.value.toLocaleString()})` : value;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-full items-center justify-center text-ga-text-muted">No data for this period</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
